import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import {
  Alert,
  Typography,
  Form,
  message,
  Row,
  Col,
  Select,
  Button,
  Modal,
  Empty,
  Radio,
  Layout,
  Spin,
} from "antd";
import { InfoCircleOutlined, DownloadOutlined } from "@ant-design/icons";
import AllApplications from "./testSitesManually";
import { downloadCSV } from "./csvDownloader";
import useLogout from "../useLogout";
import "../roleBasedTester/RoleBasedAddOTP.css";

const COUNTRY_PREFIXES = {
  "233": "Ghana",
  "223": "Mali",
  "224": "Guinea",
};

const COUNTRY_ORDER = ["Ghana", "Mali", "Guinea", "Other"];

const NUMBERS_PER_COLUMN = 50;

/** Country id hidden on Run Test for standard tester roles 4 and 9 only. */
const RUN_TEST_EXCLUDED_COUNTRY_ID = 85;
const RUN_TEST_ROLES_HIDING_COUNTRY = new Set([4, 9]);

const groupNumbersByCountry = (numbers) => {
  const groups = { Ghana: [], Mali: [], Guinea: [], Other: [] };
  numbers.forEach((num) => {
    const str = String(num);
    const prefix = str.slice(0, 3);
    const country = COUNTRY_PREFIXES[prefix] || "Other";
    groups[country].push(num);
  });
  return groups;
};

const NumbersByCountry = ({ groups, messageApi }) => (
  <>
    {COUNTRY_ORDER.map((country) => {
      const nums = groups[country] || [];
      if (nums.length === 0) return null;

      const columns = [];
      for (let i = 0; i < nums.length; i += NUMBERS_PER_COLUMN) {
        columns.push(nums.slice(i, i + NUMBERS_PER_COLUMN));
      }

      return (
        <div key={country} className="rbotp-country-section">
          <div className="rbotp-country-header">
            <span className="rbotp-country-name">{country}</span>
            <span className="rbotp-country-badge">{nums.length}</span>
            <Button
              size="small"
              onClick={() => {
                const text = nums.join("\n");
                navigator.clipboard.writeText(text).then(
                  () =>
                    messageApi.success(
                      `${country}: ${nums.length} numbers copied to clipboard`
                    ),
                  () => messageApi.error("Failed to copy")
                );
              }}
              className="rbotp-copy-btn"
            >
              Copy All
            </Button>
          </div>
          <div className="rbotp-numbers-columns">
            {columns.map((column, colIndex) => (
              <div
                key={`${country}-col-${colIndex}`}
                className="rbotp-numbers-column"
              >
                {column.map((number, index) => (
                  <div
                    key={`${country}-${colIndex}-${index}`}
                    className="rbotp-number-item"
                  >
                    {number}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      );
    })}
  </>
);

const { Title } = Typography;
const { Option } = Select;
const { Content } = Layout;

const Testers = () => {
  const logout = useLogout();
  const countriesAPI = process.env.REACT_APP_API_UpdatedCountries;
  const mnoAPI = process.env.REACT_APP_API_MobileOperators;
  const simApiUrl = process.env.REACT_APP_API_SIMCards;
  const checkIfNumsAssigned = process.env.REACT_APP_API_CheckIfNumsAssigned;
  const specialCountriesAPI =
    "https://0id4y5c0xb.execute-api.us-east-1.amazonaws.com/dev";

  const [messageApi, contextHolder] = message.useMessage();
  const [countryList, setCountryList] = useState([]);
  const [mnoNameList, setMNONameList] = useState([]);
  const [country, setCountry] = useState("");
  const [mnoObject, setMnoObject] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [isSpecialUser, setIsSpecialUser] = useState(false);
  const [phoneNumbers, setPhoneNumbers] = useState([]);
  const [countries, setCountries] = useState([]);
  const [selectedNumbers, setSelectedNumbers] = useState([]);
  const [showNumbersCard, setShowNumbersCard] = useState(false);
  const [numbersAssigned, setNumbersAssigned] = useState(false);
  const [lockNumbersModalVisible, setLockNumbersModalVisible] = useState(false);
  const [noNumbersAvailable, setNoNumbersAvailable] = useState(false);
  const [numbersLocked, setNumbersLocked] = useState(false);
  const [lockedCountries, setLockedCountries] = useState([]);
  const [loadingNumbers, setLoadingNumbers] = useState(false);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [disabledCountries, setDisabledCountries] = useState({});
  const storedUser = localStorage.getItem("user");
  const user = JSON.parse(storedUser);

  const roleId = Number(localStorage.getItem("roleId"));
  const countryOptionsForRunTest = useMemo(() => {
    if (RUN_TEST_ROLES_HIDING_COUNTRY.has(roleId)) {
      return countryList.filter(
        (c) => Number(c.id) !== RUN_TEST_EXCLUDED_COUNTRY_ID
      );
    }
    return countryList;
  }, [countryList, roleId]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const response = await axios.get(
          `${checkIfNumsAssigned}/${user.userId}`
        );

        if (response.data.inactive) {
          logout();
        } else if (response.data.body && response.data.body.length >= 100) {
          setNumbersAssigned(true);
          setPhoneNumbers(response.data.body);
          const phoneNumbers = response.data.body.map((item) => item.msisdn);
          setNumbers(phoneNumbers);
          if (user.userId === 219) {
            setNumbersLocked(true);
          }
        } else if (response.data.body && response.data.body.length === 50) {
          // Make call to the special countries API
          const specialCountriesResponse = await axios.get(
            `${specialCountriesAPI}/${user.userId}`
          );
          const { Mali, Ghana } = specialCountriesResponse.data.body;

          // Set locked countries based on the response
          const locked = [];
          if (Mali) locked.push("Mali");
          if (Ghana) locked.push("Ghana");
          setLockedCountries(locked);
        } else {
          setLoadingCountries(true);
          const responseCountries = await axios.get(countriesAPI);
          setCountries(responseCountries.data.body);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (user.userId === 219) {
      setIsSpecialUser(true);
    }
    const fetchData = async () => {
      try {
        setLoadingCountries(true);
        const responseCountries = await axios.get(countriesAPI);
        setCountryList(responseCountries.data.body);
      } catch (error) {
        console.error("Error fetching countries data:", error);
      } finally {
        setLoadingCountries(false);
      }
    };
    fetchData();
  }, []);

  const handleCountryChange = async (e) => {
    const value = e.target.value;
    setCountry(value);
    fetchMNONameList(value);
    setShowNumbersCard(false);
  };

  const handleOperatorChange = async (value) => {
    const selectedMNO = mnoNameList.find((mno) => mno.id === value);
    setMnoObject(selectedMNO);
    setShowNumbersCard(true);
    fetchPhoneNumbers(selectedMNO.id);
  };

  const fetchMNONameList = async (selectedCountry) => {
    try {
      const dataUrl = `${mnoAPI}/?country=${selectedCountry}`;
      const response = await axios.get(dataUrl);
      setMNONameList(response.data.body);
    } catch (error) {
      console.error("Error fetching MNO names:", error);
    }
  };

  const fetchPhoneNumbers = async (mnoId) => {
    setLoadingNumbers(true);
    try {
      const response = await axios.get(
        `${simApiUrl}?mno=${mnoId}&tester_id=${user.userId}`
      );

      if (response.data.body.length === 0) {
        setNoNumbersAvailable(true);
      } else {
        setNoNumbersAvailable(false);
      }

      const phoneNumbers = response.data.body.map(
        (phoneNumber) => phoneNumber.msisdn
      );
      setNumbers(phoneNumbers);

      const numbers = response.data.body.map((phoneNumber) => phoneNumber.id);
      setSelectedNumbers(numbers);
    } catch (error) {
      console.error("Error fetching phone numbers:", error);
    } finally {
      setLoadingNumbers(false);
    }
  };

  const handleLockNumbersClick = async () => {
    try {
      if (!isSpecialUser && !disabledCountries[country]) {
        setDisabledCountries((prev) => ({ ...prev, [country]: true }));
        await axios.post(`${simApiUrl}?lock=true`, {
          user_id: user.userId,
          sims_id: selectedNumbers,
        });
        setNumbersLocked(true);
        setLockedCountries([...lockedCountries, country]); // Add country to lockedCountries
      }
      if (user.userId === 219) {
        downloadCSV(numbers);
      } else {
        setLockNumbersModalVisible(true);
      }
    } catch (error) {
      console.error("Error locking numbers:", error);
    }
  };

  const handleDownloadClick = () => {
    downloadCSV(numbers);
  };

  const handleSelectApplicationsClick = () => {
    setLockNumbersModalVisible(true);
  };

  return (
    <Layout>
      {contextHolder}
      <Content
        style={{ padding: "0 18px", minHeight: "100vh", background: "#fff" }}
      >
        {numbersAssigned ? (
          <div className="rbotp">
            <div className="rbotp-card">
              <div className="rbotp-card-header">
                <h2 className="rbotp-card-title">
                  Numbers Assigned To You for Today
                </h2>
                <div className="rbotp-actions">
                  <Button
                    type="primary"
                    size="middle"
                    icon={isSpecialUser ? <DownloadOutlined /> : undefined}
                    className={
                      isSpecialUser ? "rbotp-btn-secondary" : "rbotp-btn-primary"
                    }
                    onClick={
                      isSpecialUser && numbersLocked
                        ? handleDownloadClick
                        : handleSelectApplicationsClick
                    }
                    disabled={disabledCountries[country]}
                  >
                    {isSpecialUser && numbersLocked
                      ? "Download CSV"
                      : isSpecialUser
                      ? "Download CSV"
                      : "Select Applications"}
                  </Button>
                </div>
              </div>
              <div className="rbotp-card-body">
                <NumbersByCountry
                  groups={groupNumbersByCountry(numbers)}
                  messageApi={messageApi}
                />
              </div>
            </div>
          </div>
        ) : (
          <>
            {!numbersAssigned && (
              <>
                {!isSpecialUser && (
                  <Alert
                    message="Alert: If you don't click on Lock Numbers button, the numbers will not be locked for you and you will not be able to see the generated OTPs. Therefore make sure to click on Lock Numbers."
                    type="error"
                    banner
                  />
                )}
                <Title level={4}>Numbers Assignment</Title>
                <Row gutter={16}>
                  <Col xs={{ span: 24 }} lg={{ span: 24 }}>
                    <Form.Item
                      label="Country"
                      required
                      tooltip={{
                        title: "This is a required field",
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      {loadingCountries ? (
                        <Spin size="small" />
                      ) : (
                        <Radio.Group
                          onChange={handleCountryChange}
                          value={country}
                          style={{ width: "100%" }}
                        >
                          {countryOptionsForRunTest.map((country) => (
                            <Radio.Button
                              key={country.id}
                              value={country.id}
                              disabled={lockedCountries.includes(country.name)}
                            >
                              {country.name}
                            </Radio.Button>
                          ))}
                        </Radio.Group>
                      )}
                    </Form.Item>
                  </Col>
                </Row>
                <Row gutter={16}>
                  <Col xs={{ span: 24 }} lg={{ span: 12 }}>
                    <Form.Item
                      label="MNO Name"
                      required
                      tooltip={{
                        title: "This is a required field",
                        icon: <InfoCircleOutlined />,
                      }}
                    >
                      <Select
                        showSearch
                        placeholder="Select your MNO Name"
                        value={mnoObject?.id}
                        onChange={handleOperatorChange}
                        style={{ width: "100%" }}
                        disabled={!country}
                      >
                        {mnoNameList.map((mno) => (
                          <Option key={mno.id} value={mno.id}>
                            {mno.name}
                          </Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>
                </Row>
              </>
            )}
            {showNumbersCard && (
              <>
                {loadingNumbers ? (
                  <Spin tip="Loading...">
                    <div className="rbotp-card" style={{ minHeight: 160 }} />
                  </Spin>
                ) : noNumbersAvailable ? (
                  <Empty
                    description="No numbers available for this country and network. Try a different country and network."
                    style={{ margin: "20px 0" }}
                  />
                ) : (
                  <div className="rbotp">
                    <div className="rbotp-card">
                      <div className="rbotp-card-header">
                        <h2 className="rbotp-card-title">
                          Numbers Assigned To You for Today
                        </h2>
                        <div className="rbotp-actions">
                          <Button
                            type="primary"
                            size="middle"
                            icon={
                              isSpecialUser && numbersLocked ? (
                                <DownloadOutlined />
                              ) : undefined
                            }
                            className={
                              isSpecialUser && numbersLocked
                                ? "rbotp-btn-secondary"
                                : "rbotp-btn-primary"
                            }
                            onClick={
                              isSpecialUser && numbersLocked
                                ? handleDownloadClick
                                : handleLockNumbersClick
                            }
                            disabled={disabledCountries[country]}
                          >
                            {isSpecialUser && numbersLocked
                              ? "Download CSV"
                              : isSpecialUser
                              ? "Download CSV"
                              : "Lock Numbers"}
                          </Button>
                        </div>
                      </div>
                      <div className="rbotp-card-body">
                        <NumbersByCountry
                          groups={groupNumbersByCountry(numbers)}
                          messageApi={messageApi}
                        />
                      </div>
                    </div>
                  </div>
                )}
              </>
            )}
          </>
        )}
        <Modal
          title="Select Applications"
          open={lockNumbersModalVisible}
          width={"100%"}
          footer={null}
          onCancel={() => setLockNumbersModalVisible(false)}
        >
          <AllApplications
            setShowSelectNumber={setLockNumbersModalVisible}
            numbers={numbers}
          />
        </Modal>
      </Content>
    </Layout>
  );
};

export default Testers;
